<script setup>
import { ref, computed, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const BRIDGE_BASE = '/evogo2-bridge';
const QR_POLL_MS = 2000;
const SYNC_POLL_MS = 3000;

// months (UI) -> history_days (bridge contract: 30 | 90 | 180 | 365)
const MONTHS_TO_DAYS = { 1: 30, 3: 90, 6: 180, 12: 365 };

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { accountId } = useAccount();

const step = ref(1);
const inboxName = ref('');
const instanceId = ref('');
const instanceName = ref('');
const instanceToken = ref('');
const qrDataUri = ref('');
const connected = ref(false);
const months = ref(3);
const jobId = ref('');
const syncStatus = ref('');
const syncPhase = ref('');
const contactsImported = ref(0);
const messagesImported = ref(0);
const isBusy = ref(false);

let qrTimer = null;
let syncTimer = null;
let createdInboxId = null;

const nameValid = computed(() => inboxName.value.trim().length > 0);

const qrStatusLabel = computed(() => {
  if (connected.value) return t('INBOX_MGMT.ADD.EVOGO_V2.QR.STATUS.CONNECTED');
  if (qrDataUri.value) return t('INBOX_MGMT.ADD.EVOGO_V2.QR.STATUS.QR');
  return t('INBOX_MGMT.ADD.EVOGO_V2.QR.STATUS.IDLE');
});

function stopTimers() {
  if (qrTimer) {
    clearInterval(qrTimer);
    qrTimer = null;
  }
  if (syncTimer) {
    clearInterval(syncTimer);
    syncTimer = null;
  }
}

onBeforeUnmount(stopTimers);

async function bridgeJson(path, { method = 'GET', body = null } = {}) {
  const opts = { method, headers: { Accept: 'application/json' } };
  if (body !== null) {
    opts.headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  }
  const resp = await fetch(`${BRIDGE_BASE}${path}`, opts);
  if (!resp.ok) {
    throw new Error(`Bridge ${path} -> HTTP ${resp.status}`);
  }
  return resp.json();
}

async function generateQr() {
  if (!nameValid.value || isBusy.value) return;
  isBusy.value = true;
  try {
    const data = await bridgeJson('/qr/new', {
      method: 'POST',
      body: {
        inbox_name: inboxName.value.trim(),
        account_id: accountId.value,
      },
    });
    instanceId.value = data.instance_id || '';
    instanceName.value = data.instance_name || inboxName.value.trim();
    instanceToken.value = data.instance_token || '';
    qrDataUri.value = data.qrcode || '';
    connected.value = false;
    if (!instanceToken.value) {
      throw new Error('bridge returned no instance_token');
    }
    step.value = 2;
    startQrPolling();
  } catch (_err) {
    useAlert(t('INBOX_MGMT.ADD.EVOGO_V2.ERRORS.QR_FAIL'));
  } finally {
    isBusy.value = false;
  }
}

function startQrPolling() {
  stopTimers();
  qrTimer = setInterval(async () => {
    try {
      const data = await bridgeJson(
        `/qr/status?instance_token=${encodeURIComponent(instanceToken.value)}`
      );
      connected.value = !!data.connected;
      if (!connected.value && data.qrcode) {
        qrDataUri.value = data.qrcode;
      }
      if (connected.value) {
        stopTimers();
      }
    } catch (_err) {
      // transient - next tick retries
    }
  }, QR_POLL_MS);
}

function goToPeriod() {
  if (!connected.value) return;
  step.value = 3;
}

async function startImport() {
  if (isBusy.value) return;
  isBusy.value = true;
  try {
    // Reuse inbox from previous attempt if the /sync/start failed — prevents
    // creating a duplicate Chatwoot inbox every time the user clicks retry.
    if (!createdInboxId) {
      const channel = await store.dispatch('inboxes/createChannel', {
        name: inboxName.value.trim(),
        channel: {
          type: 'api',
          additional_attributes: {
            evogo2: {
              instance_id: instanceId.value,
              instance_name: instanceName.value,
              instance_token: instanceToken.value,
              history_days: MONTHS_TO_DAYS[months.value] || 90,
            },
          },
        },
      });
      createdInboxId = channel.id;

      // Auto-add the creator as inbox member so the inbox is visible in the
      // sidebar without forcing them through the agents step. Without this the
      // freshly-created inbox has 0 members and disappears from the channel list.
      try {
        const me = store.getters.getCurrentUser;
        if (me?.id) {
          await store.dispatch('inboxMembers/create', {
            inboxId: createdInboxId,
            agentList: [me.id],
          });
        }
      } catch (_) {
        // Non-fatal — admin can still add members manually on the agents step.
      }

      // Wire real-time bridge (connector_v2) BEFORE the historical sync so new
      // WhatsApp events that arrive mid-sync land through the normal pipeline
      // (dedup by source_id protects against duplicates).
      try {
        await bridgeJson('/realtime/attach', {
          method: 'POST',
          body: {
            account_id: accountId.value,
            inbox_id: createdInboxId,
            instance_id: instanceId.value,
            instance_name: instanceName.value,
            instance_token: instanceToken.value,
          },
        });
      } catch (_) {
        // Non-fatal — sync still runs; admin can retry via the bridge endpoint.
      }
    }

    const data = await bridgeJson('/sync/start', {
      method: 'POST',
      body: {
        account_id: accountId.value,
        inbox_id: createdInboxId,
        instance_token: instanceToken.value,
        history_days: MONTHS_TO_DAYS[months.value] || 90,
      },
    });
    jobId.value = data.job_id;
    syncStatus.value = 'pending';
    step.value = 4;
    startSyncPolling();
  } catch (_err) {
    useAlert(t('INBOX_MGMT.ADD.EVOGO_V2.ERRORS.SYNC_FAIL'));
  } finally {
    isBusy.value = false;
  }
}

function skipToAgents() {
  // The bridge job continues in background — we just stop polling and navigate.
  stopTimers();
  if (createdInboxId) {
    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: createdInboxId },
    });
  }
}

function startSyncPolling() {
  stopTimers();
  syncTimer = setInterval(async () => {
    try {
      const data = await bridgeJson(
        `/sync/${encodeURIComponent(jobId.value)}/status`
      );
      syncStatus.value = data.status || syncStatus.value;
      const p = data.progress || {};
      syncPhase.value = p.phase || '';
      contactsImported.value = p.contacts_upserted || 0;
      messagesImported.value = p.messages_imported || 0;
      if (data.status === 'completed' || data.status === 'failed' || data.status === 'cancelled') {
        stopTimers();
        if (createdInboxId) {
          router.replace({
            name: 'settings_inboxes_add_agents',
            params: { page: 'new', inbox_id: createdInboxId },
          });
        }
      }
    } catch (_err) {
      // transient
    }
  }, SYNC_POLL_MS);
}
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.EVOGO_V2.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.EVOGO_V2.DESC')"
    />

    <form
      v-if="step === 1"
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="generateQr"
    >
      <div class="flex-shrink-0 flex-grow-0">
        <label>
          {{ $t('INBOX_MGMT.ADD.EVOGO_V2.NAME.LABEL') }}
          <input
            v-model="inboxName"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.EVOGO_V2.NAME.PLACEHOLDER')"
          />
        </label>
      </div>
      <div class="w-full mt-4">
        <NextButton
          :is-loading="isBusy"
          :disabled="!nameValid"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.EVOGO_V2.NAME.SUBMIT')"
        />
      </div>
    </form>

    <div v-else-if="step === 2" class="mt-6 max-w-lg">
      <h3 class="text-base font-semibold mb-2">
        {{ $t('INBOX_MGMT.ADD.EVOGO_V2.QR.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mb-4">
        {{ $t('INBOX_MGMT.ADD.EVOGO_V2.QR.DESC') }}
      </p>
      <div
        v-if="qrDataUri"
        class="inline-block p-4 bg-white border border-n-container rounded-xl"
      >
        <img :src="qrDataUri" alt="QR Code" class="w-64 h-64" />
      </div>
      <p
        class="mt-4 text-sm"
        :class="connected ? 'text-green-600' : 'text-n-slate-11'"
      >
        {{ qrStatusLabel }}
      </p>
      <div class="mt-4">
        <NextButton
          :disabled="!connected"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.EVOGO_V2.QR.CONTINUE')"
          @click="goToPeriod"
        />
      </div>
    </div>

    <div v-else-if="step === 3" class="mt-6 max-w-lg">
      <h3 class="text-base font-semibold mb-2">
        {{ $t('INBOX_MGMT.ADD.EVOGO_V2.PERIOD.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mb-4">
        {{ $t('INBOX_MGMT.ADD.EVOGO_V2.PERIOD.DESC') }}
      </p>
      <label class="block mb-4">
        <span class="text-sm">
          {{ $t('INBOX_MGMT.ADD.EVOGO_V2.PERIOD.LABEL') }}
        </span>
        <select v-model.number="months" class="mt-1 block">
          <option :value="1">
            {{ $t('INBOX_MGMT.ADD.EVOGO_V2.PERIOD.OPT_30D') }}
          </option>
          <option :value="3">
            {{ $t('INBOX_MGMT.ADD.EVOGO_V2.PERIOD.OPT_3M') }}
          </option>
          <option :value="6">
            {{ $t('INBOX_MGMT.ADD.EVOGO_V2.PERIOD.OPT_6M') }}
          </option>
          <option :value="12">
            {{ $t('INBOX_MGMT.ADD.EVOGO_V2.PERIOD.OPT_1Y') }}
          </option>
        </select>
      </label>
      <NextButton
        :is-loading="isBusy"
        solid
        blue
        :label="$t('INBOX_MGMT.ADD.EVOGO_V2.PERIOD.SUBMIT')"
        @click="startImport"
      />
    </div>

    <div v-else-if="step === 4" class="mt-6 max-w-lg">
      <h3 class="text-base font-semibold mb-2">
        {{ $t('INBOX_MGMT.ADD.EVOGO_V2.PROGRESS.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mb-4">
        {{ $t('INBOX_MGMT.ADD.EVOGO_V2.PROGRESS.DESC') }}
      </p>
      <div class="p-4 border border-n-container rounded-xl bg-n-solid-2">
        <p class="text-sm mb-2">
          {{ $t('INBOX_MGMT.ADD.EVOGO_V2.PROGRESS.STATE') }}:
          <strong>{{ syncStatus || '...' }}</strong>
          <span v-if="syncPhase" class="text-n-slate-11"> · {{ syncPhase }}</span>
        </p>
        <p class="text-sm mb-1">
          {{ $t('INBOX_MGMT.ADD.EVOGO_V2.PROGRESS.CONTACTS') }}:
          {{ contactsImported }}
        </p>
        <p class="text-sm">
          {{ $t('INBOX_MGMT.ADD.EVOGO_V2.PROGRESS.MESSAGES') }}:
          {{ messagesImported }}
        </p>
      </div>
      <div class="mt-4">
        <NextButton
          slate
          faded
          :label="$t('INBOX_MGMT.ADD.EVOGO_V2.PROGRESS.SKIP')"
          @click="skipToAgents"
        />
      </div>
    </div>
  </div>
</template>
